import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { RiSaveLine } from 'react-icons/ri'
import styled from 'styled-components'
import * as Yup from 'yup'
import { colors } from '../../../../ui-kit'
import useImageKitClient from '../../../hooks/useImageKitClient'
import useToast from '../../../hooks/useToast'
import { MLContext, UserContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import Breadcrumbs from '../MediaOperations/CopyMoveDrawer/Breadcrumbs'
import FolderElement from '../MediaOperations/CopyMoveDrawer/FolderElement'
import { secondaryFoldersPerPage } from '../../../../utils/mediaLibrary'
import { isRestrictedUser } from '../../../../utils/roles'
import { ACLPermissionEnum } from '../../../constants'

interface Props {
  image: string
  isOpen: boolean
  onClose(): void
}

interface FormProps {
  fileName: string
}

interface Upload {
  file: string
  fileName: string
  folder: string
}

const FolderContainer = styled(Box)`
  border-radius: 6px;
  border: 1px solid ${colors.appPaleLilac};
`

const SaveNewFileModal = ({
  image,
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const imagekit = useImageKitClient()
  const toast = useToast()
  const [
    {
      drawerFolderList,
      secondaryFolderPage,
      drawerFolderPath,
      drawerFolderPermission,
      refetchFiles,
    },
    dispatch,
  ] = useContext(MLContext)

  const { user } = useContext(UserContext)

  useEffect(() => {
    const controller = new AbortController()

    const eventHandler = event => {
      onClose()
      controller.abort()
    }

    window.addEventListener('popstate', eventHandler, {
      signal: controller.signal,
    })

    return () => {
      window.removeEventListener('popstate', eventHandler)
    }
  }, [])

  const upload = ({ file, fileName, folder = '/' }: Upload) =>
    new Promise((resolve, reject) => {
      imagekit.upload(
        {
          file,
          fileName,
          folder,
        },
        (err, result) => {
          if (err) {
            reject(err)
          } else {
            resolve(result)
          }
        },
      )
    })

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{
            fileName: '',
          }}
          onSubmit={async (values): Promise<void> => {
            try {
              await upload({
                file: image,
                fileName: values.fileName,
                folder: drawerFolderPath,
              })
              toast({
                description: t(
                  'file_saved_successfully',
                  'File saved successfully',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
              await new Promise(resolve => setTimeout(resolve, 1500))
              void refetchFiles()
            } catch {
              toast({
                description: t(
                  'failed_to_upload_image_message',
                  'Failed to upload image. Please try again after some time.',
                ),
                status: 'error',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
                variant: 'outline',
              })
            }
            onClose()
          }}
          validationSchema={Yup.object().shape({
            fileName: Yup.string().trim().required('Required'),
          })}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={RiSaveLine}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('save_as_new_file', 'Save as new file')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Field name="fileName">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isInvalid={Boolean(
                        form.errors.fileName && form.touched.fileName,
                      )}
                      isRequired
                    >
                      <FormLabel>{t('file_name', 'File Name')}</FormLabel>
                      <Input {...field} />
                      <FormErrorMessage marginBottom="-2.0408em">
                        {form.errors.fileName}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Text fontWeight="medium" marginTop="36px" marginBottom={1.5}>
                  {t('location', 'Location')}
                </Text>
                <FolderContainer>
                  <Box
                    padding="0.75em 0.75em"
                    backgroundColor="appPaleGreyThree"
                  >
                    <Breadcrumbs />
                  </Box>
                  <Box padding="0.75em 0.75em" height="200px" overflowY="auto">
                    {drawerFolderList.map(folder => (
                      <FolderElement folder={folder} key={folder.folderId} />
                    ))}
                    {drawerFolderList.length !== 0 &&
                    drawerFolderList.length % secondaryFoldersPerPage === 0 &&
                    drawerFolderList.length / secondaryFoldersPerPage ===
                      secondaryFolderPage + 1 ? (
                      <Flex direction="row-reverse" padding="1em 0.75em 0 0">
                        <Button
                          as="a"
                          variant="link"
                          cursor="pointer"
                          color="appClearBlue"
                          onClick={() => {
                            dispatch({
                              type: MediaLibraryActions.LOAD_MORE_SECONDARY_FOLDERS,
                            })
                          }}
                        >
                          {t('view_more', 'View More')}
                        </Button>
                      </Flex>
                    ) : null}
                    {drawerFolderList.length === 0 && (
                      <Center paddingTop="2em">
                        <Text>{t('no_folders_found', 'No Folders Found')}</Text>
                      </Center>
                    )}
                  </Box>
                </FolderContainer>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel', 'Cancel')}
                </Button>
                <Tooltip
                  label={t(
                    'upload_permission_error_message',
                    'You do not have the necessary permissions to upload a file to this location.',
                  )}
                  shouldWrapChildren
                  isDisabled={
                    !(
                      isRestrictedUser(user?.roles) &&
                      (drawerFolderPath === '/' ||
                        drawerFolderPermission === ACLPermissionEnum.READ)
                    )
                  }
                >
                  <Button
                    isDisabled={
                      isSubmitting ||
                      (isRestrictedUser(user?.roles) &&
                        (drawerFolderPath === '/' ||
                          drawerFolderPermission === ACLPermissionEnum.READ))
                    }
                    isLoading={isSubmitting}
                    onClick={() => {
                      void submitForm()
                    }}
                  >
                    {t('create', 'Create')}
                  </Button>
                </Tooltip>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default SaveNewFileModal
